import React from 'react'

import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import { Box, Checkbox, IconButton, Radio } from '@mui/material'

import { theme } from 'styles/theme'

import MobileSortControl from './MobileSortControl'
import type {
  MultiSortConfig,
  SortableColumn,
  SortConfig
} from './SortableTableHead'
import { HeadCell, Order, SelectParams } from './Table.interface'

export interface MobileCardColumn {
  id: string
  /**
   * Rótulo em texto puro. `HeadCell.label` pode ser um ReactNode (tooltips,
   * ícones, gatilhos), que não serve como rótulo dentro do card — por isso o
   * texto vem separado.
   */
  label: string
}

export interface MobileCardsConfig {
  /**
   * Colunas na ordem em que a tabela as renderiza. Quando ausente, são
   * derivadas do `header`.
   */
  columns?: MobileCardColumn[]
  /** Coluna que vira o título do card. Default: a primeira não-especial. */
  titleColumn?: string
  /** Coluna alinhada ao topo direito do card (ex.: status). */
  badgeColumn?: string
  /** Coluna que vira o rodapé de ações do card. */
  actionsColumn?: string
  /** Colunas que não aparecem no card (ruído em tela pequena). */
  hiddenColumns?: string[]
  /** Alinhamento do rodapé de ações. Default: centralizado. */
  actionsAlign?: 'start' | 'center' | 'end'
  /** Desliga o modo card e mantém a tabela com scroll horizontal. */
  enabled?: boolean
  /**
   * A tela já oferece o botão Ordenar na barra de filtros, então o card não
   * desenha o dele. Sem isso apareceriam dois controles na mesma tela.
   */
  sortInToolbar?: boolean
}

/** `true` liga o modo card com tudo inferido do `header`. */
export type MobileCardsProp = boolean | MobileCardsConfig

// Chaves que as tabelas consomem como metadados da linha, não como célula.
// Mesma lista que <EnhancedTable> e <Table> desestruturam antes de renderizar.
const RESERVED_ROW_KEYS = new Set(['uuid', 'backgroundColor', 'clickable'])

/**
 * Quantas linhas um valor de texto ocupa antes de cortar com reticências.
 * Três é o que cabe sem o card perder a proporção de item de lista: a maioria
 * dos valores tem uma linha só, e os poucos textos longos param de empurrar os
 * outros campos para fora da tela.
 */
const VALUE_LINE_CLAMP = 3

/** Idem para o nome no cabeçalho do card. O apelido abaixo dele fica em uma. */
const TITLE_LINE_CLAMP = 2

const FOOTER_JUSTIFY: Record<'start' | 'center' | 'end', string> = {
  start: 'flex-start',
  center: 'center',
  end: 'flex-end'
}

const normalize = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase()

const BADGE_TERMS = ['status', 'situacao', 'situation']
// Colunas que identificam a linha ("de quem é isso"). Deliberadamente curta:
// termos genéricos como "descrição" roubariam o título de colunas melhores.
const TITLE_TERMS = [
  'customer',
  'customer_name',
  'customername',
  'cliente',
  'name',
  'nome',
  'razao social',
  'razao_social'
]
const ACTIONS_TERMS = [
  'actions',
  'action',
  'acoes',
  'acao',
  'options',
  'opcoes'
]

/**
 * Extrai o texto de um `HeadCell.label`. Headers com tooltip ou ícone chegam
 * como elemento; o texto que interessa costuma ser a primeira string dentro.
 */
const headerText = (label: React.ReactNode): string => {
  if (typeof label === 'string') return label
  if (typeof label === 'number') return String(label)
  if (Array.isArray(label)) {
    for (const item of label) {
      const text = headerText(item)
      if (text) return text
    }
    return ''
  }
  if (React.isValidElement(label)) {
    return headerText((label.props as { children?: React.ReactNode })?.children)
  }
  return ''
}

/**
 * Um valor "vazio" precisa virar travessão para as linhas do card não
 * desalinharem. As células chegam como ReactNode (`<p>{formatDate('')}</p>`),
 * então a checagem desce até os filhos: elemento sem filhos (ícone, avatar,
 * chip) conta como conteúdo, elemento cujos filhos são vazios não.
 */
const isEmptyCell = (node: React.ReactNode): boolean => {
  if (node === null || node === undefined || node === false || node === '')
    return true
  if (typeof node === 'string') return node.trim() === ''
  if (Array.isArray(node)) return node.every(isEmptyCell)
  if (React.isValidElement(node)) {
    const children = (node.props as { children?: React.ReactNode })?.children
    if (children === undefined) return false
    return isEmptyCell(children)
  }
  return false
}

/** Mesmo desempacotamento que as células da tabela fazem. */
const cellContent = (value: unknown): React.ReactNode => {
  if (value && typeof value === 'object' && 'component' in value) {
    return (value as { component: React.ReactNode }).component
  }
  return value as React.ReactNode
}

const matches = (column: MobileCardColumn, terms: string[]) => {
  const id = normalize(column.id)
  const label = normalize(column.label)
  return terms.includes(id) || terms.includes(label)
}

/**
 * Normaliza a prop `mobileCards` em uma configuração completa, derivando do
 * `header` o que não foi informado. Devolve `null` quando o modo card está
 * desligado — aí a tabela renderiza normalmente.
 *
 * A inferência é conservadora: procura uma coluna de status para o badge e uma
 * de ações para o rodapé (por id ou por rótulo), e usa a primeira coluna
 * restante como título. Qualquer papel pode ser fixado na config.
 */
export const resolveMobileCards = (
  prop: MobileCardsProp | undefined,
  header: HeadCell[]
): MobileCardsConfig | null => {
  if (!prop) return null
  const config: MobileCardsConfig = prop === true ? {} : prop
  if (config.enabled === false) return null

  const columns: MobileCardColumn[] =
    config.columns ??
    (header || []).map((cell) => ({
      id: cell.id,
      label: headerText(cell.label)
    }))

  if (!columns.length) return null

  const findId = (terms: string[]) =>
    columns.find((column) => matches(column, terms))?.id

  // Só a ÚLTIMA coluna pode ser o rodapé de ações. Sem isso, uma coluna de
  // conteúdo chamada "Ação" (o histórico da cobrança tem uma) viraria rodapé e
  // levaria o texto embora. Rótulo vazio na última coluna também conta: é como
  // as tabelas do projeto marcam a coluna de ícones.
  const lastColumn = columns[columns.length - 1]
  const lastIsActions =
    columns.length > 1 &&
    (matches(lastColumn, ACTIONS_TERMS) || lastColumn.label.trim() === '')
  const actionsColumn =
    config.actionsColumn ?? (lastIsActions ? lastColumn.id : undefined)

  const badgeColumn = config.badgeColumn ?? findId(BADGE_TERMS)

  const isBody = (column: MobileCardColumn) =>
    column.id !== actionsColumn && column.id !== badgeColumn
  const titleColumn =
    config.titleColumn ??
    columns.find((column) => isBody(column) && matches(column, TITLE_TERMS))
      ?.id ??
    columns.find(isBody)?.id

  return { ...config, columns, titleColumn, badgeColumn, actionsColumn }
}

interface MobileCardListProps {
  rows?: any[]
  config: MobileCardsConfig
  page?: number
  rowsPerPage?: number
  totalCount?: number
  handleChangePage?: (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ) => void
  hiddePagination?: boolean
  select: SelectParams
  rowAction?: (uuid: string) => void
  // === Ordenação ===
  /** Quais colunas o backend aceita ordenar. Sem isso não há botão Ordenar. */
  sortableColumns?: SortableColumn[]
  sortConfig?: SortConfig | MultiSortConfig
  onSort?: (field: string, direction: Order) => void
  onMultiSort?: (sorts: MultiSortConfig) => void
  disableSort?: boolean
}

const MobileCardList: React.FC<MobileCardListProps> = ({
  rows = [],
  config,
  page = 0,
  rowsPerPage = 25,
  totalCount = 0,
  handleChangePage,
  hiddePagination = false,
  select,
  rowAction,
  sortableColumns,
  sortConfig,
  onSort,
  onMultiSort,
  disableSort
}) => {
  const { useSelect, selected, setSelected, singleSelect } = select
  const {
    columns = [],
    titleColumn,
    badgeColumn,
    actionsColumn,
    hiddenColumns = [],
    actionsAlign = 'center'
  } = config

  const indexOf = (id?: string) =>
    id ? columns.findIndex((column) => column.id === id) : -1

  const titleIndex = indexOf(titleColumn) >= 0 ? indexOf(titleColumn) : 0
  const badgeIndex = indexOf(badgeColumn)
  const actionsIndex = indexOf(actionsColumn)
  const hiddenIndexes = new Set(
    [
      titleIndex,
      badgeIndex,
      actionsIndex,
      ...hiddenColumns.map(indexOf)
    ].filter((index) => index >= 0)
  )

  const isSelected = (uuid: string) => !!selected && selected.includes(uuid)

  const toggleSelected = (uuid: string) => {
    if (!setSelected) return
    setSelected((prev) => {
      const current = prev.selected || []
      if (singleSelect) {
        return { ...prev, selected: current.includes(uuid) ? [] : [uuid] }
      }
      return {
        ...prev,
        selected: current.includes(uuid)
          ? current.filter((id) => id !== uuid)
          : [...current, uuid]
      }
    })
  }

  const from = totalCount === 0 ? 0 : page * rowsPerPage + 1
  const to = Math.min(totalCount, (page + 1) * rowsPerPage)
  const isLastPage = to >= totalCount

  return (
    <Box>
      {/* No card não existe cabeçalho para clicar; a ordenação vem para cá —
          a menos que a tela já ofereça o botão na barra de filtros. */}
      {!config.sortInToolbar && (
        <MobileSortControl
          columns={columns}
          sortableColumns={sortableColumns}
          sortConfig={sortConfig}
          onSort={onSort}
          onMultiSort={onMultiSort}
          disableSort={disableSort}
        />
      )}

      <Box display="flex" flexDirection="column" gap="12px">
        {rows.map((row, index) => {
          const { uuid, backgroundColor, clickable } = row
          const isRowClickable = clickable === undefined ? true : !!clickable
          const canOpen = !useSelect && isRowClickable && !!rowAction
          const isItemSelected = isSelected(String(uuid))

          // As tabelas renderizam as células pela ORDEM das chaves da linha, em
          // paralelo com o `header` — não pelo nome. O card segue a mesma
          // convenção, senão tabelas cujas chaves não batem com os ids das
          // colunas mostrariam campos vazios.
          const cellKeys = Object.keys(row).filter(
            (key) => !RESERVED_ROW_KEYS.has(key)
          )
          const cellAt = (columnIndex: number) =>
            columnIndex >= 0 && columnIndex < cellKeys.length
              ? cellContent(row[cellKeys[columnIndex]])
              : undefined

          const handleCardClick = () => {
            // Linha marcada como não clicável (desabilitada) ignora o toque:
            // nem seleciona, nem abre.
            if (!isRowClickable) return
            if (useSelect) {
              toggleSelected(String(uuid))
              return
            }
            if (canOpen) rowAction?.(String(uuid))
          }

          const badge = cellAt(badgeIndex)
          const actions = cellAt(actionsIndex)

          return (
            <Box
              component="article"
              key={uuid || index}
              onClick={handleCardClick}
              sx={{
                backgroundColor: backgroundColor || '#fff',
                border: `1px solid ${
                  isItemSelected
                    ? theme.palette.primary[200]
                    : theme.palette.neutral[700]
                }`,
                borderRadius: '12px',
                padding: '12px 14px',
                opacity: isRowClickable ? 1 : 0.4,
                cursor:
                  isRowClickable && (useSelect || canOpen)
                    ? 'pointer'
                    : 'default',
                transition: 'border-color 120ms ease, background-color 120ms',
                // As células vêm da tabela com `<p>` e o projeto não tem reset
                // global: sem isso a margem do user agent (1em) empurra cada
                // valor para baixo e desalinha os pares rótulo/valor.
                '& p': { margin: 0 },
                '&:active': {
                  backgroundColor:
                    isRowClickable && (useSelect || canOpen)
                      ? theme.palette.neutral[900]
                      : backgroundColor || '#fff'
                }
              }}
            >
              {/* Cabeçalho: seleção + título à esquerda, badge à direita */}
              <Box display="flex" alignItems="flex-start" gap="8px">
                {useSelect &&
                  (singleSelect ? (
                    <Radio
                      color="primary"
                      checked={isItemSelected}
                      disabled={!isRowClickable}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => toggleSelected(String(uuid))}
                      sx={{ padding: 0, marginTop: '2px' }}
                    />
                  ) : (
                    <Checkbox
                      color="primary"
                      checked={isItemSelected}
                      disabled={!isRowClickable}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => toggleSelected(String(uuid))}
                      sx={{ padding: 0, marginTop: '2px' }}
                    />
                  ))}
                <Box
                  flex={1}
                  minWidth={0}
                  sx={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: theme.palette.neutral[100],
                    lineHeight: 1.35,
                    // `word-break: break-word` equivale a
                    // `overflow-wrap: anywhere`, que entra na conta do
                    // min-content: o apelido "6517" era espremido a ponto de
                    // quebrar em "65 / 17". `break-word` só quebra quando a
                    // palavra realmente não cabe sozinha na linha.
                    overflowWrap: 'break-word',
                    // A célula de título é um flex horizontal (avatar + nome +
                    // apelido + selo) desenhado para a largura da coluna. No
                    // card não cabe em uma linha, e sem `wrap` os itens se
                    // espremem em vez de descer.
                    '& > *': { maxWidth: '100%', flexWrap: 'wrap' },
                    // Razão social longa ("JIG SOLAR - JONES ISMAEL GENZ
                    // INSTALACAO DE GERADORES FOTOVOLTAICOS") ocupava quatro
                    // linhas, e o apelido — que muitas vezes repete o nome —
                    // mais três. O cabeçalho ficava maior que o card inteiro.
                    // O nome é `<p>` e o apelido `<span>` em todas as tabelas,
                    // então o corte segue a hierarquia: o nome tem direito a
                    // duas linhas, o apelido a uma.
                    '& p': {
                      display: '-webkit-box',
                      WebkitLineClamp: TITLE_LINE_CLAMP,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden'
                    },
                    '& span': {
                      display: '-webkit-box',
                      WebkitLineClamp: 1,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden'
                    }
                  }}
                >
                  {cellAt(titleIndex)}
                </Box>
                {!isEmptyCell(badge) && (
                  <Box flexShrink={0} sx={{ fontSize: '12px' }}>
                    {badge}
                  </Box>
                )}
              </Box>

              {/* Corpo: rótulo à esquerda, valor ancorado na borda direita.
                  Coluna fixa (38%) fazia o valor começar no meio do card —
                  data e dinheiro ficavam soltos e e-mail quebrava sem
                  precisar. Com o valor ocupando o espaço que sobra e alinhado
                  à direita, os valores formam uma coluna só. */}
              <Box
                display="flex"
                flexDirection="column"
                rowGap="8px"
                marginTop="12px"
                sx={{ '&:empty': { display: 'none' } }}
              >
                {columns.map((column, columnIndex) => {
                  if (hiddenIndexes.has(columnIndex)) return null
                  const value = cellAt(columnIndex)
                  return (
                    <Box
                      key={column.id || columnIndex}
                      display="flex"
                      alignItems="baseline"
                      justifyContent="space-between"
                      columnGap="16px"
                      rowGap="4px"
                      // Um chip é `white-space: nowrap` e não encolhe. Quando
                      // não sobrava largura para ele ("Meio de pagamento" +
                      // "Recebimento manual"), o excesso saía para a ESQUERDA
                      // — é assim que overflow se comporta com alinhamento à
                      // direita — e o chip cobria o rótulo. Deixando a linha
                      // quebrar, o que não cabe ao lado do rótulo desce para
                      // uma linha própria de largura inteira em vez de
                      // sobrepor. Valores de texto não descem: encolhem, e a
                      // quebra é decidida pelo tamanho natural do conteúdo.
                      flexWrap="wrap"
                    >
                      <Box
                        flexShrink={0}
                        sx={{
                          fontSize: '13px',
                          color: theme.palette.neutral[400],
                          lineHeight: 1.45
                        }}
                      >
                        {column.label}
                      </Box>
                      <Box
                        minWidth={0}
                        sx={{
                          // `0 1 auto` e não `1 1 auto`: a caixa tem a largura
                          // do próprio conteúdo. É o que faz o alinhamento se
                          // resolver sozinho, sem CSS saber se a linha quebrou
                          // ou não — quem posiciona é o `space-between` da
                          // linha. Cabendo ao lado do rótulo, a caixa vai para
                          // a direita e os valores formam uma coluna só;
                          // descendo para uma linha própria, ela fica sozinha
                          // e o `space-between` a encosta na esquerda, que é
                          // como um bloco (grupo de chips, texto de várias
                          // linhas) se lê. Com `1 1 auto` a caixa ocupava a
                          // linha inteira e o conteúdo ficava jogado na
                          // direita, esparramado.
                          flex: '0 1 auto',
                          fontSize: '13px',
                          fontWeight: 500,
                          color: theme.palette.neutral[100],
                          lineHeight: 1.45,
                          textAlign: 'left',
                          overflowWrap: 'anywhere',
                          // Muitas células são desenhadas para a largura da
                          // coluna da tabela e truncam ali: a descrição da
                          // recorrência, por exemplo, é `width: 35ch` +
                          // `nowrap` + `ellipsis`. No card 35ch passa da borda
                          // e o texto vaza. `maxWidth` é o que segura a largura
                          // (não briga com ninguém: `max-width` sempre limita
                          // `width`); as outras três desfazem o truncamento
                          // para o texto voltar a quebrar linha, e precisam de
                          // `!important` porque empatam em especificidade com a
                          // classe da própria célula. `justifyContent` desfaz o
                          // alinhamento que as células flex trazem da tabela
                          // (onde encostam na borda da linha): aqui quem
                          // posiciona a caixa é a linha, e dentro dela o
                          // conteúdo começa da esquerda — é o que mantém um
                          // grupo de chips de duas fileiras alinhado.
                          '& > *': {
                            maxWidth: '100%',
                            justifyContent: 'flex-start',
                            whiteSpace: 'normal !important',
                            overflow: 'visible !important',
                            textOverflow: 'clip !important'
                          },
                          // Desfazer o truncamento acima devolve a quebra de
                          // linha, mas uma descrição de recorrência com 1500
                          // caracteres passa a ocupar a tela inteira. No
                          // desktop a mesma celula corta com reticencias
                          // (`width: 35ch` + `ellipsis`); aqui ela corta por
                          // altura, que e o equivalente em uma coluna que nao
                          // tem largura fixa. O texto todo continua a um toque
                          // de distancia, no detalhe que o card abre.
                          //
                          // `> p` e nao `> *` de proposito: `-webkit-box`
                          // trocaria o `display` de celulas que sao flex (o
                          // link do e-mail, o telefone) e quebraria o
                          // alinhamento delas. As celulas de texto do projeto
                          // sao `<p>`, e o seletor mais especifico ainda vence
                          // o `overflow: visible` de cima.
                          // Chip largo demais até para uma linha inteira
                          // volta a poder quebrar. `> * >` alcança o chip
                          // dentro do <ChipList> sem virar uma regra solta em
                          // qualquer profundidade.
                          '& > * > *': { whiteSpace: 'normal !important' },
                          '& > p': {
                            display: '-webkit-box',
                            WebkitLineClamp: VALUE_LINE_CLAMP,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden !important',
                            textOverflow: 'ellipsis !important'
                          },
                          // Várias células embrulham o valor num link com
                          // `padding: 18px 0` inline — é o que dá altura à
                          // LINHA da tabela. Dentro do card isso vira um vão
                          // entre o rótulo e o valor (o e-mail do cliente
                          // descia uma linha inteira). Só `!important` vence
                          // um style inline.
                          '& a': {
                            paddingTop: '0 !important',
                            paddingBottom: '0 !important'
                          }
                        }}
                      >
                        {isEmptyCell(value) ? (
                          <span style={{ color: theme.palette.neutral[500] }}>
                            —
                          </span>
                        ) : (
                          value
                        )}
                      </Box>
                    </Box>
                  )
                })}
              </Box>

              {/* Rodapé: ações centralizadas, com área de toque confortável */}
              {!isEmptyCell(actions) && (
                <Box
                  marginTop="12px"
                  paddingTop="8px"
                  borderTop={`1px solid ${theme.palette.neutral[700]}`}
                  onClick={(e) => e.stopPropagation()}
                  sx={{
                    display: 'flex',
                    justifyContent: FOOTER_JUSTIFY[actionsAlign],
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '4px',
                    // A célula de ações da tabela é um flex `width: 100%` com
                    // alinhamento próprio — quase sempre à direita, porque na
                    // tabela ela encosta na borda da linha. No card quem manda é
                    // o rodapé. Precisa de `!important`: a classe que o emotion
                    // gera para a própria célula empata em especificidade com
                    // `& > *`, e quem vencia era a ordem de inserção — os ícones
                    // ficavam à direita.
                    '& > *': {
                      justifyContent: `${FOOTER_JUSTIFY[actionsAlign]} !important`,
                      flexWrap: 'wrap'
                    },
                    // As colunas de ação usam ButtonRounded (minWidth 46px,
                    // padding 12px 16px), pensado para a célula da tabela. No
                    // card isso espalha os ícones; aqui eles viram um grupo
                    // compacto e uniforme, sem perder a área de toque.
                    '& .MuiButtonBase-root': {
                      minWidth: '40px',
                      height: '40px',
                      padding: '8px'
                    }
                  }}
                >
                  {actions}
                </Box>
              )}
            </Box>
          )
        })}
      </Box>

      {!hiddePagination && (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          gap="12px"
          marginTop="16px"
        >
          <IconButton
            aria-label="Página anterior"
            disabled={page === 0}
            onClick={(e) => handleChangePage?.(e, page - 1)}
            size="small"
          >
            <KeyboardArrowLeftIcon fontSize="small" />
          </IconButton>
          <Box
            sx={{
              fontSize: '13px',
              color: theme.palette.neutral[400],
              whiteSpace: 'nowrap'
            }}
          >
            {`${from}–${to} de ${totalCount}`}
          </Box>
          <IconButton
            aria-label="Próxima página"
            disabled={isLastPage}
            onClick={(e) => handleChangePage?.(e, page + 1)}
            size="small"
          >
            <KeyboardArrowRightIcon fontSize="small" />
          </IconButton>
        </Box>
      )}
    </Box>
  )
}

export default MobileCardList
